// math-eval.ts — Clickable math formula evaluation (DOM + editor glue).
//
// Formulas ending with "=" become clickable. On click the code resolves
// variable dependencies across all inline ($…$) and block ($$…$$) formulas
// in the note, then evaluates the expression and shows the result (≤ 10
// significant figures) or "?" when unsolvable. This works in BOTH preview
// (the `.katex` click layer here) and in edit mode (the InlineMathWidget,
// which routes its clicks through the editor's undoable transaction path).
//
// The pure math core — extraction, LaTeX→JS translation, the CSP-safe
// numeric evaluator, variable resolution, formatting, and the shared
// `isEvaluableMath` / `evaluateMathExpression` predicates — lives in
// `web/js/utils/math-expr.js`. This module imports those and re-exports them
// so existing importers (markdown-renderer.js, export-import.js) keep their
// paths, while adding the DOM/editor-state behaviour that can't live in a
// leaf utility. Mermaid variable substitution stays here too — it encodes
// mermaid-identifier knowledge, not generic math.

import { previewDiv } from '../core/app-state.js';
import {
  buildMathVariableMap,
  evaluateInlineWithColumns,
  evaluateLatexExpr,
  evaluateMathExpression,
  expandLatexFrac,
  expandLatexSqrt,
  extractAllMathExpressions,
  formatMathResult,
  isEvaluableMath,
  latexToJsExpr,
  parseBraceGroup,
  substituteVarsInLatex,
} from '../utils/math-expr.js';
import { markOverflowingMathContainers } from './math-overflow.js';
import { debounce } from '../utils/debounce.js';
// Mermaid variable substitution + the KaTeX overflow scan now live in
// note-editor-FREE leaf modules so the editor widgets (and render-core/mermaid)
// can import them without closing an import cycle through note-editor.js. They
// are re-imported + re-exported here so existing importers of math-eval.js
// (markdown-renderer.js, export-import.js, the wheel/resize observers below)
// keep their paths unchanged.
import { buildMermaidVarMap, substituteVarsInMermaid } from './math-substitute.js';
import { NoteEditor } from './note-editor.js';
import { normalizeWheelDelta } from './wheel-delta.js';

// Render the LIVE value of an open (trailing `=`) equation into the preview,
// DISPLAY-ONLY (#5). Routes through THE shared inline-with-columns evaluator
// (the same leaf the editor widget + export use) against the note-wide column
// env — so a prose formula reading a named table column (`$total = sum(Price)$`)
// shows the identical scalar everywhere. A scalar result renders a `.math-result`
// element immediately (KaTeX or text); a vector / unresolved result renders
// NOTHING (matching the editor, which shows no live value for those). There is
// NO click listener and the source is NEVER written — the value is a pure,
// recomputed-on-render display.
function makeFormulaClickable(container: any, texSource: any, env: any): void {
  // KaTeX wraps block math in `.katex-display` (a span/div) containing the inner
  // `.katex`; inline math is just `.katex`. The container is the outermost one.
  const isDisplay = container.classList.contains('katex-display');
  const r = evaluateInlineWithColumns(texSource, env);
  if (!r.ok || r.kind !== 'scalar') return; // vector / unresolved → no live value

  const resultEl = document.createElement(isDisplay ? 'div' : 'span');
  resultEl.classList.add('math-result');
  if (isDisplay) resultEl.classList.add('math-result-block');
  container.after(resultEl);

  if (window.katex) {
    try {
      window.katex.render(r.formatted, resultEl, {
        displayMode: isDisplay,
        throwOnError: false,
        output: 'html',
        strict: 'ignore',
      });
      markOverflowingMathContainers(resultEl);
    } catch {
      resultEl.textContent = r.formatted;
    }
  } else {
    resultEl.textContent = r.formatted;
  }
}

function setupClickableMathFormulas(): void {
  const content = NoteEditor.getContent();
  const mathExprs = extractAllMathExpressions(content) as any[];
  if (mathExprs.length === 0) return;

  // B5-Layer-1: the INTERACTIVE preview HAS editor state, so read the cached
  // note-wide column env (scalars + data columns + resolved formula columns)
  // from `tableMathEnvField` via the guarded accessor instead of re-scanning
  // getContent() to rebuild it — removing one redundant rebuild so edit and
  // preview can't diverge. Falls back to a scalar-only varMap when the editor
  // env isn't available (test mocks / pre-init); a scalar map is a valid env
  // for the shared evaluator (it binds names → numbers).
  //
  // NOTE: this is the editor-backed interactive path ONLY. The HEADLESS
  // export/graph/print path (render-core / export-import.js) has NO editor
  // state and MUST keep its own env rebuild — do not "unify" them.
  const env = NoteEditor.getColumnMathEnv?.() || buildMathVariableMap(mathExprs);
  // Select the outermost KaTeX container for each formula: block math is
  // `.katex-display`, which itself wraps a nested `.katex`. Picking the
  // direct children of every sentinel `<span data-katex-hash>` gives one
  // container per source fragment, matching mathExprs by index. Falling
  // back to a flat scan covers stray formulas (e.g. from auto-render in
  // table cells) that aren't wrapped in a sentinel span.
  let containers = Array.from(
    previewDiv.querySelectorAll(
      'span[data-katex-hash] > .katex-display, span[data-katex-hash] > .katex'
    )
  );
  if (containers.length === 0) {
    containers = Array.from(
      previewDiv.querySelectorAll('.katex-display, .katex:not(.katex-display .katex)')
    );
  }

  containers.forEach((container: any, idx: any) => {
    const annotation = container.querySelector('annotation[encoding="application/x-tex"]');
    // Inline fragments are typeset with a leading `\displaystyle` (readable
    // fractions — see math.ts). Defensive strip: with `output:'html'` KaTeX emits
    // no x-tex annotation so this is a no-op today (texSource falls through to
    // mathExpr.tex, the raw source), but it keeps the eval source clean if the
    // KaTeX output mode ever changes to include the annotation.
    let texSource = (annotation?.textContent ?? '').replace(/^\\displaystyle\s*/, '');
    const mathExpr = idx < mathExprs.length ? mathExprs[idx] : null;
    if (!texSource && mathExpr) texSource = mathExpr.tex;

    // Shared predicate with edit mode + export — an open (trailing `=`) equation
    // shows its live value. Whitespace-tolerant, so `$x = $` matches too.
    if (isEvaluableMath(texSource)) {
      makeFormulaClickable(container, texSource.trim(), env);
    }
  });
}

// markOverflowingMathContainers now lives in the note-editor-free leaf
// editor/math-overflow.js (imported + re-exported above). The editor widgets
// import it directly from that leaf; the wheel/resize observers below use the
// imported binding. No window.* shim is needed anymore — its only readers were
// the editor widgets, which now import the leaf.

function setupMathWheelScroll(root: any): void {
  if (!root || typeof root.addEventListener !== 'function') return;
  root.addEventListener(
    'wheel',
    (e: any) => {
      const container = (e.target as any).closest('.katex-display, .katex');
      if (!container) return;
      // Only intercept when the formula is marked as overflowing (set by markOverflowingMathContainers).
      if (!container.classList.contains('math-overflow')) return;
      e.preventDefault();
      container.scrollLeft += normalizeWheelDelta(e);
    },
    { passive: false }
  );
}

function setupMathResizeObserver(root: any): void {
  if (!root || typeof ResizeObserver === 'undefined') return;
  const observer = new ResizeObserver(debounce(() => markOverflowingMathContainers(root), 150));
  observer.observe(root);
}

// Register wheel-scroll and resize-observer handlers on both the preview
// pane and the live editor so overflowing formulas scroll horizontally
// the same way in both views.
document.addEventListener('DOMContentLoaded', () => {
  setupMathWheelScroll(previewDiv);
  setupMathResizeObserver(previewDiv);
  const cmEditor = document.getElementById('cm-editor');
  if (cmEditor) {
    setupMathWheelScroll(cmEditor);
    setupMathResizeObserver(cmEditor);
  }
});

export {
  buildMathVariableMap,
  // ── Re-exported note-editor-free leaves (kept on this path for callers) ──
  buildMermaidVarMap, // → math-substitute.js
  evaluateInlineWithColumns,
  evaluateLatexExpr,
  evaluateMathExpression,
  expandLatexFrac,
  expandLatexSqrt,
  // ── Re-exported pure core (single source of truth: utils/math-expr.js) ──
  // Kept on this path so existing importers (markdown-renderer.js,
  // export-import.js) don't need repointing.
  extractAllMathExpressions,
  formatMathResult,
  isEvaluableMath,
  latexToJsExpr,
  makeFormulaClickable,
  markOverflowingMathContainers, // → math-overflow.js
  parseBraceGroup,
  // ── Editor/DOM-bound behaviour (lives here) ─────────────────────────────
  setupClickableMathFormulas,
  substituteVarsInLatex,
  substituteVarsInMermaid, // → math-substitute.js
};
